import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from './app/hooks';
import { selectAuth } from './store/slices/authSlice';
import { initializeWebSocket } from './store/slices/messagesSlice';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import ChatLayout from './pages/ChatLayout';
import Contacts from './pages/Contacts';
import Templates from './pages/Templates';
import TemplateCreate from './pages/TemplateCreate';
import Campaigns from './pages/Campaigns';
import CampaignCreate from './pages/CampaignCreate';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import PhoneConfig from './pages/PhoneConfig';
import WhatsAppWebSessions from './pages/WhatsAppWebSessions';
import WhatsAppWebSetup from './pages/WhatsAppWebSetup';
import CatalogManagement from './pages/CatalogManagement';
import CatalogItemEditor from './pages/CatalogItemEditor';
import OrderManagement from './pages/OrderManagement';
import AdminGuard from './components/AdminGuard';
import AdminDashboard from './pages/AdminDashboard';
import AdminUsers from './pages/AdminUsers';
import AdminLogs from './pages/AdminLogs';
import AdminHealth from './pages/AdminHealth';

// Auth guard for protected routes
const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading, token } = useAppSelector(selectAuth);
  const dispatch = useAppDispatch();
  
  // Initialize WebSocket connection when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      console.log('Initializing WebSocket connection');
      dispatch(initializeWebSocket(token));
    }
  }, [isAuthenticated, token, dispatch]);
  
  // Show loading indicator while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-whatsapp-green"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Protected routes */}
      <Route path="/" element={
        <AuthGuard>
          <Dashboard />
        </AuthGuard>
      } />
      <Route path="/dashboard" element={
        <AuthGuard>
          <Dashboard />
        </AuthGuard>
      } />
      <Route path="/messages" element={
        <AuthGuard>
          <ChatLayout />
        </AuthGuard>
      } />
      <Route path="/messages/:contactId" element={
        <AuthGuard>
          <ChatLayout />
        </AuthGuard>
      } />
      <Route path="/contacts" element={
        <AuthGuard>
          <Contacts />
        </AuthGuard>
      } />
      <Route path="/templates" element={
        <AuthGuard>
          <Templates />
        </AuthGuard>
      } />
      <Route path="/templates/create" element={
        <AuthGuard>
          <TemplateCreate />
        </AuthGuard>
      } />
      <Route path="/templates/edit/:id" element={
        <AuthGuard>
          <TemplateCreate />
        </AuthGuard>
      } />
      <Route path="/campaigns" element={
        <AuthGuard>
          <Campaigns />
        </AuthGuard>
      } />
      <Route path="/campaigns/create" element={
        <AuthGuard>
          <CampaignCreate />
        </AuthGuard>
      } />
      <Route path="/campaigns/edit/:id" element={
        <AuthGuard>
          <CampaignCreate />
        </AuthGuard>
      } />
      <Route path="/analytics" element={
        <AuthGuard>
          <Analytics />
        </AuthGuard>
      } />
      <Route path="/settings" element={
        <AuthGuard>
          <Settings />
        </AuthGuard>
      } />
      <Route path="/phones" element={
        <AuthGuard>
          <PhoneConfig />
        </AuthGuard>
      } />
      <Route path="/whatsapp-web" element={
        <AuthGuard>
          <WhatsAppWebSessions />
        </AuthGuard>
      } />
      <Route path="/whatsapp-web/new" element={
        <AuthGuard>
          <WhatsAppWebSetup />
        </AuthGuard>
      } />
      <Route path="/whatsapp-web/:sessionId" element={
        <AuthGuard>
          <WhatsAppWebSetup />
        </AuthGuard>
      } />
      
      {/* Catalog Management Routes */}
      <Route path="/catalog" element={
        <AuthGuard>
          <CatalogManagement />
        </AuthGuard>
      } />
      <Route path="/catalog/new" element={
        <AuthGuard>
          <CatalogItemEditor />
        </AuthGuard>
      } />
      <Route path="/catalog/edit/:itemId" element={
        <AuthGuard>
          <CatalogItemEditor />
        </AuthGuard>
      } />
      <Route path="/orders" element={
        <AuthGuard>
          <OrderManagement />
        </AuthGuard>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin/dashboard" element={
        <AuthGuard>
          <AdminGuard>
            <AdminDashboard />
          </AdminGuard>
        </AuthGuard>
      } />
      <Route path="/admin/users" element={
        <AuthGuard>
          <AdminGuard>
            <AdminUsers />
          </AdminGuard>
        </AuthGuard>
      } />
      <Route path="/admin/logs" element={
        <AuthGuard>
          <AdminGuard>
            <AdminLogs />
          </AdminGuard>
        </AuthGuard>
      } />
      <Route path="/admin/health" element={
        <AuthGuard>
          <AdminGuard>
            <AdminHealth />
          </AdminGuard>
        </AuthGuard>
      } />
      
      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
};

export default AppRouter;
